import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  PLATFORM_ID, OnDestroy, OnInit,
} from '@angular/core';
import { UpaAsyncService } from '../../services/upa-async.service';
import { ActivatedRoute } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { RequestDataService } from '../../services/request-data.service';
import { HttpClient } from '@angular/common/http';
import { NewRelicService } from '../../services/newrelic.service';
import { HeaderLoyaltyComponent } from './header-loyalty/header-loyalty.component';
import { HeaderLandingComponent } from './header-landing/header-landing.component';
import { YourPointsComponent } from './your-points/your-points.component';
import { MissionsComponent } from './missions/missions.component';
import { CouponsComponent } from './coupons/coupons.component';
import { ClubBenefitsComponent } from './club-benefits/club-benefits.component';
import { FaqComponent } from './faq/faq.component';
import { redirectToUrl } from './utils/navigation.utils';

@Component({
  selector: 'al-ifood-landing',
  imports: [
    CommonModule,
    HeaderLoyaltyComponent,
    HeaderLandingComponent,
    YourPointsComponent,
    MissionsComponent,
    CouponsComponent,
    ClubBenefitsComponent,
    FaqComponent
  ],
  standalone: true,
  templateUrl: './ifood-landing.component.html',
  styleUrls: ['./ifood-landing.component.scss']
})
export class IfoodLandingComponent implements OnDestroy, OnInit {
  private requestDataService = inject(RequestDataService);
  private platformId = inject(PLATFORM_ID);
  private activatedRoute = inject(ActivatedRoute);
  readonly deviceType = this.requestDataService.getDeviceType();
  readonly source = this.activatedRoute.snapshot.queryParams['source'] || 'unknown';

  isResolutionMobile = false;
  showHeader = true;

  upaAsyncService = inject(UpaAsyncService);
  newrelicService = inject(NewRelicService);
  route = inject(ActivatedRoute);
  http = inject(HttpClient);

  isClube: boolean | null = null;
  isEnrolled: boolean | null = null;
  isLoggedIn: boolean | null = null;
  points: number | null = null;
  ces: number | null = null;
  isShareWithFriends: boolean | null = null;
  isTakeExclusiveOffers: boolean | null = null;
  isClient = false;
  loadingIfoodInfo = true;
  ifoodInfoError = false;

  private boundCheckResolutionMobile!: () => void;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.source =
        this.route.snapshot.queryParamMap.get('source') || 'unknown';
      this.getIfoodLandingInfoFromBackend();
      this.newrelicService.setCustomAttribute(
        'pageview_id',
        this.requestDataService.getPageviewId()
      );
      this.newrelicService.setCustomAttribute(
        'operation_id',
        this.requestDataService.getOperationId()
      );
      this.newrelicService.setCustomAttribute(
        'uow',
        this.requestDataService.getUow()
      );
    }
  }

  private dispatchAppNavbarEvent(): void {
    const event = new window.CustomEvent('app.navbar', {
      detail: {
        main_view: {
          title: 'Benefícios Passaporte',
        },
        product: 'hotels',
        flow: 'landing',
        pageview_id: this.requestDataService.getPageviewId(),
      },
    });
    document.dispatchEvent(event);
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.showHeader = !this.requestDataService.isWebView();
      if (this.requestDataService.isWebView()) {
        this.dispatchAppNavbarEvent();
      }
      this.boundCheckResolutionMobile = this.checkResolution.bind(this);
      this.boundCheckResolutionMobile();
      window.addEventListener('resize', this.boundCheckResolutionMobile);
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('resize', this.boundCheckResolutionMobile);
    }
  }

  private getIfoodLandingInfoFromBackend() {
    this.loadingIfoodInfo = true;
    this.http
      .get<{
        isClube: boolean;
        isEnrolled: boolean;
        isLoggedIn: boolean;
        points: number;
        ces: number;
        isShareWithFriends: boolean;
        isTakeExclusiveOffers: boolean;
      }>('/travel-board/ifood-landing', {
        headers: {
          'x-client': 'account-linker',
        },
      })
      .subscribe({
        next: (info) => {
          const trackData = {
            source: this.source,
            device_type: this.deviceType.toString(),
            isClube: info?.isClube ?? undefined,
            isEnrolled: info?.isEnrolled ?? undefined,
            isLoggedIn: info?.isLoggedIn ?? undefined,
            points: info?.points ?? undefined,
            ces: info?.ces ?? undefined,
            isShareWithFriends: info?.isShareWithFriends ?? undefined,
            isTakeExclusiveOffers: info?.isTakeExclusiveOffers ?? undefined,
          };
          this.upaAsyncService
            .trackToGeneric(
              'system-action',
              'pageview',
              'pageview',
              'ifood-landing',
              trackData
            )
            .subscribe();
          if (info) {
            this.isClube = info.isClube;
            this.isEnrolled = info.isEnrolled;
            this.isLoggedIn = info.isLoggedIn;
            this.points = info.points;
            this.ces = info.ces;
            this.isShareWithFriends = info.isShareWithFriends;
            this.isTakeExclusiveOffers = info.isTakeExclusiveOffers;
          } else {
            this.ifoodInfoError = true;
          }
          this.isClient = this.ces !== null && this.ces > 4;
          this.loadingIfoodInfo = false;
        },
        error: () => {
          this.ifoodInfoError = true;
          this.loadingIfoodInfo = false;
        },
      });
  }

  checkResolution() {
    if (isPlatformBrowser(this.platformId)) {
      this.isResolutionMobile = this.requestDataService.getDeviceType() === 'mobile';
    }
  }

  get isClientNotEnrolled(): boolean {
    return !this.isEnrolled && this.ces !== null && this.ces > 4;
  }

  get showClubeEnrolledCashback(): boolean {
    return (
      this.isClube === true &&
      this.isLoggedIn === true &&
      this.isEnrolled === true
    );
  }

  get showNoClubeEnrolledCashback(): boolean {
    return (
      this.isClube === false &&
      this.isLoggedIn === true &&
      this.isEnrolled === true
    );
  }

  get showClubeNotEnrolledNotClientCashback(): boolean {
    return (
      this.isClube === true &&
      this.isLoggedIn === true &&
      this.isEnrolled === false &&
      this.isClient === false
    );
  }

  get showLoggedInEnrolled(): boolean {
    return (
      this.isLoggedIn === true &&
      this.isEnrolled === true
    );
  }

  get showNoLoggedInEnrolled(): boolean {
    return (
      this.isLoggedIn === false &&
      this.isEnrolled === true
    );
  }

  get showNoClubeNotEnrolledNotClientCashback(): boolean {
    return (
      this.isClube === false &&
      this.isLoggedIn === true &&
      this.isEnrolled === false &&
      this.isClient === false
    );
  }

}
